<template>
  <UiComponentWrapper
    class="page"
    :class="data.page.themeColor.color"
  >
    <!-- Design > Gradient -->
    <UiComponentWrapper class="gradient" />

    <!-- Section > Jumbotron -->
    <UiComponentContainer class="jumbotron">
      <UiComponentWrapper class="content">
        <OrganismHero
          alignment="left"
          :primary-button="{
            ...signupOrUpgradeCallToAction,
            label: 'Header section CTA',
          }"
          :secondary-button="{
            ...requestDemoCallToAction,
            label: 'Header section CTA',
          }"
        >
          <template #tag>
            {{ data.page.shortName }}
          </template>
          <template #heading>
            {{ data.page.title }}
          </template>
          <template #paragraph>
            {{ data.page.description }}
          </template>
        </OrganismHero>
        <UiComponentWrapper class="review">
          <UiComponentWrapper
            class="rating"
            :data-hover="data.componentCustomersReviewLogo.hoverText"
          >
            <UiComponentWrapper class="stars">
              <img
                v-for="n in 4"
                :key="n"
                alt="star"
                height="20"
                loading="lazy"
                src="@/assets/svg/star.svg"
                width="20"
              >
              <img
                alt="star"
                height="20"
                loading="lazy"
                src="@/assets/svg/star-not-complete.svg"
                width="20"
              >
            </UiComponentWrapper>
            <div
              class="copy"
              v-html="`${data.componentCustomersReviewLogo.from} ${data.componentCustomersReviewLogo.count}`"
            />
          </UiComponentWrapper>
          <UiComponentWrapper class="customers">
            <UiComponentWrapper
              v-for="{ id, logo, name } in heroLogos"
              :key="id"
              class="logo"
            >
              <UiComponentImage
                :alt="name"
                contains
                :expected-size="48"
                :extra-params="{
                  con: 100,
                  gam: 100,
                  monochrome: '000000',
                  sat: -100,
                  vib: 100,
                }"
                :height="logo.height"
                :orientation="
                  logo.width > logo.height ? 'portrait' : 'landscape'
                "
                :source="logo.url"
                use-natural-width
                :width="logo.width"
              />
            </UiComponentWrapper>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper class="showcase">
        <UiComponentImage
          v-if="data.page.image && data.page.image.url"
          :alt="data.page.title"
          class="cover"
          :expected-size="heroImageWidth"
          :height="data.page.image.height"
          :source="data.page.image.url"
          :width="data.page.image.width"
        />
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Benefits -->
    <UiComponentContainer class="benefits">
      <UiComponentWrapper class="inner">
        <UiComponentImage
          v-if="data.page.themeColor.background"
          :key="`${benefitsBackgroundHeight}-${benefitsBackgroundWidth}`"
          :alt="data.page.themeColor.background.alt"
          :expected-size="benefitsBackgroundWidth / 3"
          :extra-params="{
            rect: `${benefitsBackgroundX},${benefitsBackgroundY},${benefitsBackgroundWidth},${benefitsBackgroundHeight}`,
            fit: 'crop',
          }"
          :height="benefitsBackgroundHeight"
          :orientation="
            benefitsBackgroundWidth > benefitsBackgroundHeight
              ? 'landscape'
              : 'portrait'
          "
          :quality="[100, 80, 80]"
          :source="data.page.themeColor.background.url"
          :width="benefitsBackgroundWidth"
        />
        <OrganismHero
          alignment="left"
          :level="2"
          :primary-button="{
            ...signupOrUpgradeCallToAction,
            label: 'Benefits section CTA',
          }"
          :secondary-button="{
            ...requestDemoCallToAction,
            label: 'Benefits section CTA',
          }"
        >
          <template #tag>
            {{ data.componentGlobal.benefit }}
          </template>
          <template #heading>
            {{ data.page.benefitsTitle }}
          </template>
          <template #paragraph>
            {{ data.page.benefitsText }}
          </template>
        </OrganismHero>
        <UiComponentWrapper class="list">
          <UiComponentWrapper
            v-for="{ id, text, title } in data.page.benefitsItems"
            :key="id"
            class="item"
          >
            <UiComponentHeading :level="3">
              {{ title }}
            </UiComponentHeading>
            <UiComponentParagraph>{{ text }}</UiComponentParagraph>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Stats -->
    <UiComponentContainer class="stats">
      <MoleculeStatsBanner>
        <template #percent>
          {{ data.page.statsKeyNumber }}
        </template>
        <template #percentText>
          {{ data.page.statsTitle }}
        </template>
        <template #paragraph>
          {{ data.page.statsText }}
        </template>
        <template #infos>
          {{ data.page.statsSource }}
        </template>
      </MoleculeStatsBanner>
    </UiComponentContainer>

    <!-- [Conditional] Section > Sub Industries -->
    <UiComponentContainer
      v-if="relatedSubIndustries.length > 0"
      class="related-sub-industries"
    >
      <OrganismHero :level="2">
        <template #heading>
          {{ data.page.subIndustriesTitle }}
        </template>
        <template #paragraph>
          {{ data.page.subIndustriesText }}
        </template>
      </OrganismHero>

      <UiComponentWrapper class="list">
        <MoleculeSubIndustryCard
          v-for="
            { id, heroImage: image, heroTag: title, heroText: text, slug } in relatedSubIndustries
          "
          :key="`sub-industry-${id}`"
          class="card"
          :image="image"
          :title="title"
          :text="text"
          :slug="slug"
        >
          {{ data.componentGlobal.learnMore }}
        </MoleculeSubIndustryCard>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Features -->
    <UiComponentContainer class="features">
      <OrganismHero
        alignment="left"
        :level="2"
      >
        <template #tag>
          {{ data.componentGlobal.features }}
        </template>
        <template #heading>
          {{ data.page.featuresTitle }}
        </template>
        <template #paragraph>
          {{ data.page.featuresText }}
        </template>
      </OrganismHero>
      <UiComponentWrapper class="list">
        <UiComponentWrapper
          v-for="feature in features"
          :key="feature.id"
          class="item"
        >
          <UiComponentWrapper class="text">
            <UiComponentHeading :level="3">
              {{ feature.title }}
            </UiComponentHeading>
            <UiComponentParagraph>{{ feature.text }}</UiComponentParagraph>
          </UiComponentWrapper>
          <UiComponentImage
            v-if="feature.image"
            :key="`feature-image-${feature}-${featuresImagesSize}`"
            :alt="feature.image.alt"
            class="illustration"
            :expected-size="featuresImagesSize"
            :height="feature.image.height"
            :quality="[90, 40, 20]"
            :source="feature.image.url"
            :width="feature.image.width"
          />
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Value Proposition -->
    <OrganismValueProposition />

    <!-- Section > Related Use Cases -->
    <UiComponentContainer
      class="use-cases"
      :class="{ extended: relatedProductPages.length > 0 }"
    >
      <OrganismHero
        alignment="center"
        :level="2"
      >
        <template #tag>
          {{ data.componentWording.tag }}
        </template>
        <template #heading>
          {{ data.page.relatedUseCasesTitle }}
        </template>
      </OrganismHero>
      <UiComponentWrapper class="mobile">
        <UiComponentWrapper
          v-for="{
            id,
            themeColor,
            icon,
            shortName,
            description,
            slug,
          } in data.page.relatedUseCases"
          :key="`mobile-${id}`"
          class="use-case"
          :class="themeColor.color"
        >
          <UiComponentWrapper class="background">
            <UiComponentIcon
              v-if="icon"
              :type="icon.name"
            />
          </UiComponentWrapper>
          <UiComponentHeading
            as="paragraph"
            :level="3"
          >
            {{ shortName }}
          </UiComponentHeading>
          <UiComponentParagraph>{{ description }}</UiComponentParagraph>
          <AtomButton
            type="text"
            :to="
              localePath({
                name: 'use-cases-slug',
                params: {
                  slug,
                },
              })
            "
          >
            {{ data.componentGlobal.learnMore }}
            <UiComponentIcon type="arrow-right" />
          </AtomButton>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper class="desktop">
        <UiComponentWrapper class="list">
          <UiComponentWrapper
            v-for="({ id, shortName }, index) in data.page.relatedUseCases"
            :key="`desktop-item-${id}`"
            class="item"
            :class="{
              active: index === relatedUseCasesActiveIndex,
            }"
            @click="relatedUseCasesGlide.go(`=${index}`)"
          >
            <UiComponentParagraph>{{ shortName }}</UiComponentParagraph>
          </UiComponentWrapper>
        </UiComponentWrapper>
        <div class="slider glide">
          <div
            class="inner glide__track"
            data-glide-el="track"
          >
            <div class="flex glide__slides">
              <div
                v-for="{
                  id,
                  themeColor,
                  icon,
                  shortName,
                  description,
                  image,
                  slug,
                } in data.page.relatedUseCases"
                :key="`desktop-slide-${id}`"
                class="glide__slide swiper-slide use-case"
                :class="themeColor.color"
              >
                <UiComponentWrapper class="content">
                  <UiComponentWrapper class="background">
                    <UiComponentIcon
                      v-if="icon"
                      :type="icon.name"
                    />
                  </UiComponentWrapper>
                  <UiComponentHeading :level="3">
                    {{ shortName }}
                  </UiComponentHeading>
                  <UiComponentParagraph>{{ description }}</UiComponentParagraph>
                  <AtomButton
                    type="text"
                    :to="
                      localePath({
                        name: 'use-cases-slug',
                        params: {
                          slug,
                        },
                      })
                    "
                  >
                    {{ data.componentGlobal.learnMore }}
                    <UiComponentIcon type="arrow-right" />
                  </AtomButton>
                </UiComponentWrapper>
                <UiComponentImage
                  v-if="image"
                  :alt="image.alt"
                  class="picture"
                  :expected-size="682"
                  :height="image.height"
                  :source="image.url"
                  :width="image.width"
                />
              </div>
            </div>
          </div>
        </div>
      </UiComponentWrapper>
      <UiComponentWrapper
        v-if="relatedProductPages.length > 0"
        class="product"
      >
        <UiComponentHeading :level="3">
          {{
            data.componentWording.relatedProductPageTitle
          }}
        </UiComponentHeading>
        <UiComponentWrapper class="list">
          <AtomAnchor
            v-for="{ id, icon, title, to } in relatedProductPages"
            :key="`product-item-${id}`"
            class="item"
            :to="to"
          >
            <img
              :alt="title"
              class="icon"
              :height="16"
              :src="icon"
              :width="16"
            >{{ title }}
          </AtomAnchor>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Reviews -->
    <UiComponentContainer class="reviews">
      <MoleculeReviewG2 :medals="data.componentCertification.medals">
        <template #tag>
          {{ data.componentCertification.tag }}
        </template>
        <template #heading>
          {{
            data.page.customersReviewsSectionTitle || data.componentCertification.title
          }}
        </template>
      </MoleculeReviewG2>
      <OrganismTestimonials
        :testimonials="testimonials"
        :to="localePath('customers')"
      >
        <template #testimonial>
          {{ data.componentGlobal.readTheCustomerStory }}
        </template>
        <template #call-to-action>
          {{ data.componentGlobal.readTheirStories }}
        </template>
      </OrganismTestimonials>
    </UiComponentContainer>

    <!-- [Conditional] Section > FAQ -->
    <UiComponentWrapper
      v-if="faqItems.length > 0"
      class="frequently-asked-question"
    >
      <UiComponentContainer
        class="inner"
        :start="1"
        :end="1"
      >
        <UiComponentHeading :level="2">
          {{ data.page.faqTitle || data.componentGlobal.faqTitle }}
        </UiComponentHeading>
        <OrganismAccordion :items="faqItems" />
        <AtomButton
          v-if="hasMoreFaqItems"
          to="#"
          type="secondary"
          @click="toggleFaqItems"
        >
          {{
            showMoreFaqItems
              ? data.componentGlobal.faqShowLessQuestions
              : data.componentGlobal.faqShowMoreQuestions
          }}
        </AtomButton>
      </UiComponentContainer>
    </UiComponentWrapper>

    <!-- Section > Get Started -->
    <UiComponentContainer
      class="get-started"
      :start="1"
      :end="1"
    >
      <OrganismCallToAction
        :features="livestormFeatures"
        :primary-button="{
          ...signupOrUpgradeCallToAction,
          label: 'Big section CTA',
        }"
        :secondary-button="{
          ...requestDemoCallToAction,
          label: 'Big section CTA',
        }"
        theme="new"
      >
        <template #heading>
          {{ data.componentGetStarted.title }}
        </template>
        <template #paragraph>
          {{ data.componentGetStarted.paragraph }}
        </template>
      </OrganismCallToAction>
    </UiComponentContainer>

    <!-- Section > Related Posts -->
    <OrganismHighlightedPosts
      v-if="(data.page.relatedPosts.length >= 4 || data.componentRelatedPosts.items.length > 0) && locale !== 'de'"
      class="our-guides"
      :items="
        data.page.relatedPosts.length >= 4
          ? data.page.relatedPosts
          : data.componentRelatedPosts.items
      "
      :minutes-read="data.componentGlobal.minRead"
    >
      {{ data.page.relatedPostsTitle || data.componentRelatedPosts.title }}
    </OrganismHighlightedPosts>

    <!-- [Conditional] Section > SEO Items -->
    <UiComponentContainer
      v-if="data.page.seoItems.length > 0"
      class="seo-items"
      :start="1"
      :end="1"
    >
      <UiComponentWrapper class="list">
        <UiComponentWrapper
          v-for="{ id, title, titleLevel, text, image, video } in data.page.seoItems"
          :key="`seo-item-${id}`"
          class="item"
        >
          <UiComponentHeading :level="titleLevel || 2">
            {{ title }}
          </UiComponentHeading>
          <div
            class="text"
            v-html="$md.render(text)"
          />
          <UiComponentWrapper
            v-if="image"
            class="image"
          >
            <UiComponentWrapper class="inner">
              <UiComponentImage
                :key="`seo-item-${id}-image-${seoItemImageSize}`"
                :alt="image.alt"
                :expected-size="seoItemImageSize"
                :height="image.height"
                :orientation="image.width >= image.height ? 'landscape' : 'portrait'"
                :source="image.url"
                :width="image.width"
              />
            </UiComponentWrapper>
          </UiComponentWrapper>
          <UiComponentWrapper
            v-if="video && video.provider === 'youtube'"
            class="video"
          >
            <UiComponentWrapper class="inner">
              <iframe
                :src="`https://www.youtube.com/embed/${video.providerUid}`"
                title="YouTube video player"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerpolicy="strict-origin-when-cross-origin"
                allowfullscreen
              />
            </UiComponentWrapper>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentContainer>
  </UiComponentWrapper>
</template>

<script setup>
import Glide from '@glidejs/glide'

// Layout
definePageMeta({
  layout: 'default',
  middleware: [
    'check-if-the-industry-exists',
  ],
})

// i18n
defineI18nRoute({
  paths: {
    de: '/branchen/[slug]',
    en: '/industries/[slug]',
    es: '/sectores/[slug]',
    fr: '/secteurs/[slug]',
  },
})

// Composables
const { page } = useAnalyticsJS()
const { setAvailableLanguages } = useAvailableLanguages()
const { updateListItems } = useBreadcrumb()
const { state } = useConditionalPreviewMode()
const { locale } = useI18n()
const { isLogged } = useIsLoggedIn()
const localePath = useLocalePath()
const { $md } = useMarkdown()
const { meta, params } = useRoute()
const getRouteBaseName = useRouteBaseName()
const { public: publicRuntimeConfig } = useRuntimeConfig()
const { width: screenWidth } = useScreenSize()

// Async Data
const { data } = await useFetch(`/api/content/pages/industries/${locale.value}`, {
  key: `page-industries-[slug]-${params.slug}-${locale.value}`,
  query: {
    preview: state.preview,
  },
  transform: (input) => {
    const { allIndustries, page: hub, ...props } = input
    const page = allIndustries.find(({ slug }) => slug === params.slug)
    return {
      ...props,
      hub,
      page,
    }
  },
})

if (!data.value?.page) {
  throw createError({ statusCode: 404, message: 'Page not found' })
}

// Meta
const { alternativeVersions } = data.value.page
const locales = alternativeVersions.map(({ locale }) => locale)

meta.nuxtI18n = alternativeVersions.reduce(
  (accumator, { locale, value: slug }) => ({
    ...accumator,
    [locale]: { slug },
  }),
  {},
)

const head = useLocaleHead({
  identifierAttribute: 'id',
  addSeoAttributes: true,
})

useSeoMeta({
  title: data.value.page.titleSeo,
  description: data.value.page.descriptionSeo,
  ogTitle: data.value.page.titleSeo,
  ogDescription: data.value.page.descriptionSeo,
  twitterTitle: data.value.page.titleSeo,
  twitterDescription: data.value.page.descriptionSeo,
})

useHead({
  link: head.value.link
    .filter((link) => {
      return link.rel === 'canonical'
        || (link.rel === 'alternate' && link.hreflang === 'x-default')
        || (link.rel === 'alternate' && locales.includes(link.hreflang))
    })
    .map((link) => {
      return {
        ...link,
        href: !locales.includes('en') && link.id === 'i18n-xd'
          ? `${publicRuntimeConfig.baseUrl}${localePath({ name: getRouteBaseName() })}`
          : link.href,
      }
    }),
  meta: head.value.meta.filter((meta) => {
    return meta.property === 'og:url'
      || meta.property === 'og:locale'
      || (meta.property === 'og:locale:alternate' && locales.includes(meta.content))
  }),
  script: [
    data.value.page.faqItems.length > 0
      ? {
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            'mainEntity': data.value.page.faqItems.map(item => ({
              '@type': 'Question',
              'name': item.title,
              'acceptedAnswer': {
                '@type': 'Answer',
                'text': $md.render(item.text),
              },
            })),
          }),
          type: 'application/ld+json',
        }
      : {},
    {
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': data.value.home.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('index')}`,
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': data.value.hub.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('industries')}`,
          },
          {
            '@type': 'ListItem',
            'position': 3,
            'name': data.value.page.trackingBreadcrumbTitle || data.value.page.shortName,
          },
        ],
      }),
      type: 'application/ld+json',
    },
  ],
})

// Refs
const benefitsBackgroundHeight = ref(0)
const benefitsBackgroundWidth = ref(0)
const benefitsBackgroundX = ref(0)
const benefitsBackgroundY = ref(0)
const benefitsObserver = ref(null)
const relatedUseCasesActiveIndex = ref(0)
const relatedUseCasesGlide = ref(null)
const showMoreFaqItems = ref(false)

// Computed Properties
const faqItems = computed(() => {
  const index = showMoreFaqItems.value ? data.value.page.faqItems.length : 5
  return data.value.page.faqItems
    .slice(0, index)
    .map(item => ({
      title: item.title,
      content: $md.render(item.text),
    }))
})

const features = computed(() => {
  return data.value.page.features.map(feature => ({
    id: feature.id,
    image: feature.image,
    title: feature.title,
    text: `${feature.firstText}\n\n${feature.secondText}`,
  }))
})

const featuresImagesSize = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return Math.min(screenWidth.value - 48, 630)
    default:
      return 730
  }
})

const hasMoreFaqItems = computed(() => {
  return data.value.page.faqItems.length > 5
})

const heroImageWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1024:
      return screenWidth.value - 48
      break
    case screenWidth.value >= 1024 && screenWidth.value < 1328:
      return (screenWidth.value - 48) * (620 / 1328)
    default:
      return 620
  }
})

const heroLogos = computed(() => {
  return data.value.page.highlightedCustomers
    .filter(({ logo }) => !!logo)
    .map(({ id, logo, name }) => ({
      id,
      logo,
      name,
    }))
})

const livestormFeatures = computed(() => {
  return [data.value.componentGlobal.noCreditCard, data.value.componentGlobal.noInstall]
})

const relatedProductPages = computed(() => {
  return data.value.allProductPages.map(({ id, slug, title }) => {
    let icon = ''
    let to = ''
    switch (true) {
      case slug === 'online-meeting-software':
        icon
          = 'https://livestorm.imgix.net/1127/1718714876-virtual-meetings.svg'
        to = localePath({
          name: 'online-meeting-software',
        })
        break
      case slug === 'virtual-event-software':
        icon
          = 'https://livestorm.imgix.net/1127/1718714875-virtual-events.svg'
        to = localePath({
          name: 'virtual-event-software',
        })
        break
      case slug === 'webinar-software':
        icon = 'https://livestorm.imgix.net/1127/1718714870-webinars.svg'
        to = localePath({
          name: 'webinar-software',
        })
        break
      case slug === 'automated-webinar':
        icon
          = 'https://livestorm.imgix.net/1127/1718714872-automated-webinars.svg'
        to = localePath({
          name: 'webinar-software-automated-webinar',
        })
        break
      case slug === 'on-demand-webinar':
        icon
          = 'https://livestorm.imgix.net/1127/1718714873-on-demand-webinars.svg'
        to = localePath({
          name: 'webinar-software-on-demand',
        })
        break
      case slug === 'restreaming':
        icon
          = 'https://livestorm.imgix.net/1127/1718701819-restreaming.svg'
        to = localePath({
          name: 'restreaming',
        })
        break
      case slug === 'embed':
        icon
          = 'https://livestorm.imgix.net/1127/1718701818-embed.svg'
        to = localePath({
          name: 'embed',
        })
        break
    }
    return {
      id,
      icon,
      title,
      to,
    }
  })
})

const relatedSubIndustries = computed(() => {
  return data.value.allSubIndustries
    .filter(({ relatedIndusty }) => relatedIndusty.id === data.value.page.id)
    .filter((_, index) => index < 3)
})

const requestDemoCallToAction = computed(() => {
  return {
    action: 'Demo clicked',
    slot: data.value.componentGlobal.ctaGetDemo,
    to: '#form-demo',
  }
})

const seoItemImageSize = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return Math.min(Math.max(screenWidth.value - 48, 1), 840)
    default:
      return 840
  }
})

const signupOrUpgradeCallToAction = computed(() => {
  return isLogged.value
    ? {
        action: 'Upgrade clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/settings?page=settings&tab=billing`,
        slot: data.value.componentGlobal.ctaUpgradeNow,
      }
    : {
        action: 'Signup clicked',
        to: `https://${publicRuntimeConfig.appBaseUrl}/#/signup`,
        slot: data.value.componentGlobal.ctaGetStartedForFree,
      }
})

const testimonials = computed(() => {
  return data.value.page.testimonials.map(({ citation, customer }) => ({
    customer: {
      avatar: {
        alt: `${customer.authorUserStory}' Avatar`,
        height: customer.authorPicture.height,
        url: customer.authorPicture.url,
        width: customer.authorPicture.width,
      },
      name: customer.authorUserStory,
      position: customer.authorPosition,
    },
    logo: {
      alt: `${customer.name}' logo`,
      height: customer.logoColor.height,
      url: customer.logoColor.url,
      width: customer.logoColor.width,
    },
    quote: citation || customer.quote,
    slug:
      customer?.contentUserStory.length > 0
        ? localePath({
            name: 'customers-slug',
            params: { slug: customer?.slug },
          })
        : undefined,
  }))
})

// Methods
function onObserveBenefitsSection() {
  const dpr = 3
  const { clientWidth } = document.documentElement
  const { offsetHeight, offsetWidth }
    = document.querySelector('.page .benefits .inner')
  benefitsBackgroundHeight.value = offsetHeight * dpr
  benefitsBackgroundWidth.value = offsetWidth * dpr
  switch (true) {
    case clientWidth < 768:
      benefitsBackgroundX.value = 1392 * dpr
      benefitsBackgroundY.value = 1816 * dpr
      break
    case clientWidth >= 768 && clientWidth < 1328:
      benefitsBackgroundX.value = 1392 * dpr
      benefitsBackgroundY.value = 2000 * dpr
      break
    default:
      benefitsBackgroundX.value = 933 * dpr
      benefitsBackgroundY.value = 2019 * dpr
  }
}

function toggleFaqItems() {
  showMoreFaqItems.value = !showMoreFaqItems.value
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  benefitsObserver.value.unobserve(document.querySelector('.page .benefits'))
  relatedUseCasesGlide.value?.destroy()
})

onMounted(() => {
  page(data.value.page?.trackName || data.value.page?.trackNameFallback)
  setAvailableLanguages(data.value.page.alternativeVersions.map(({ locale }) => locale))
  updateListItems([
    {
      item: localePath('index'),
      name: data.value.home.trackingBreadcrumbTitle,
    },
    {
      item: localePath('industries'),
      name: data.value.hub.trackingBreadcrumbTitle,
    },
    {
      item: localePath({ name: getRouteBaseName() }),
      name: data.value.page.trackingBreadcrumbTitle || data.value.page.shortName,
    },
  ])

  benefitsObserver.value = new ResizeObserver(onObserveBenefitsSection)
  benefitsObserver.value.observe(document.querySelector('.page .benefits'))

  if (document.querySelector('.page .use-cases')) {
    relatedUseCasesGlide.value = new Glide('.use-cases .desktop .glide', {
      type: 'slider',
      gap: 0,
      perView: 1,
    })
    relatedUseCasesGlide.value.on('run.after', () => {
      relatedUseCasesActiveIndex.value = relatedUseCasesGlide.value.index
    })
    relatedUseCasesGlide.value.mount()
  }
})
</script>

<style lang="postcss" scoped>
@import "../../../assets/css/glide.css";

.page {
  @apply relative flex-col items-center;

  &.blizzard {
    .gradient {
      background: linear-gradient(
        180deg,
        #f0f6fa 0%,
        rgba(240, 246, 250, 0) 100%
      );
    }

    :deep(.tag) {
      @apply bg-blizzard-100 text-blizzard-700;
    }

    :deep(strong) {
      @apply text-blizzard-700;
    }

    :deep(.jumbotron) {
      .hero {
        .tag {
          @apply bg-blizzard-200;
        }
      }

      .showcase {
        &::before {
          background: radial-gradient(
            50% 50% at 50% 50%,
            #d9e9f2 0%,
            rgba(217, 233, 242, 0) 100%
          );
        }
      }
    }

    :deep(.benefits) {
      .inner {
        @apply bg-blizzard-100;
      }
    }

    :deep(.stats) {
      .stats-banner {
        .highlighted {
          @apply bg-blizzard-500;
        }
      }
    }

    :deep(.features) {
      .list {
        .item {
          .illustration {
            @apply bg-blizzard-100 border-blizzard-200;
          }
        }
      }
    }

    :deep(.value-proposition) {
      .benefits {
        .headline {
          &::after {
            @apply bg-blizzard-700;
          }
        }
      }
    }
  }

  &.grey-blue {
    .gradient {
      background: linear-gradient(
        180deg,
        #f6f7f9 0%,
        rgba(246, 247, 249, 0) 100%
      );
    }

    :deep(.tag) {
      @apply bg-grey-blue-100 text-grey-blue-600;
    }

    :deep(strong) {
      @apply text-grey-blue-600;
    }

    :deep(.jumbotron) {
      .hero {
        .tag {
          @apply bg-grey-blue-200;
        }
      }

      .showcase {
        &::before {
          background: radial-gradient(
            50% 50% at 50% 50%,
            #eaeef1 0%,
            rgba(234, 238, 241, 0) 100%
          );
        }
      }
    }

    :deep(.benefits) {
      .inner {
        @apply bg-grey-blue-100;
      }
    }

    :deep(.stats) {
      .stats-banner {
        .highlighted {
          @apply bg-grey-blue-500;
        }
      }
    }

    :deep(.features) {
      .list {
        .item {
          .illustration {
            @apply bg-grey-blue-100 border-grey-blue-200;
          }
        }
      }
    }

    :deep(.value-proposition) {
      .benefits {
        .headline {
          &::after {
            @apply bg-grey-blue-700;
          }
        }
      }
    }
  }

  &.livestorm-blue {
    .gradient {
      background: linear-gradient(
        180deg,
        #f0f4ff 0%,
        rgba(240, 244, 255, 0) 100%
      );
    }

    :deep(.tag) {
      @apply bg-livestorm-blue-100 text-livestorm-blue-600;
    }

    :deep(strong) {
      @apply text-livestorm-blue-600;
    }

    :deep(.jumbotron) {
      .hero {
        .tag {
          @apply bg-livestorm-blue-200;
        }
      }

      .showcase {
        &::before {
          background: radial-gradient(
            50% 50% at 50% 50%,
            #dce6fe 0%,
            rgba(220, 230, 254, 0) 100%
          );
        }
      }
    }

    :deep(.benefits) {
      .inner {
        @apply bg-livestorm-blue-100;
      }
    }

    :deep(.stats) {
      .stats-banner {
        .highlighted {
          @apply bg-livestorm-blue-500;
        }
      }
    }

    :deep(.features) {
      .list {
        .item {
          .illustration {
            @apply bg-livestorm-blue-100 border-livestorm-blue-200;
          }
        }
      }
    }

    :deep(.value-proposition) {
      .benefits {
        .headline {
          &::after {
            @apply bg-livestorm-blue-700;
          }
        }
      }
    }
  }

  &.live-red {
    .gradient {
      background: linear-gradient(
        180deg,
        #fdf7f7 70.6%,
        rgba(253, 247, 247, 0) 100%
      );
    }

    :deep(.tag) {
      @apply bg-live-red-100 text-live-red-600;
    }

    :deep(strong) {
      @apply text-live-red-600;
    }

    :deep(.jumbotron) {
      .hero {
        .tag {
          @apply bg-live-red-200;
        }
      }

      .showcase {
        &::before {
          background: radial-gradient(
            50% 50% at 50% 50%,
            rgba(190, 58, 55, 0.16) 0%,
            rgba(190, 58, 55, 0) 100%
          );
        }
      }
    }

    :deep(.benefits) {
      .inner {
        @apply bg-live-red-100;
      }
    }

    :deep(.stats) {
      .stats-banner {
        .highlighted {
          @apply bg-live-red-500;
        }
      }
    }

    :deep(.features) {
      .list {
        .item {
          .illustration {
            @apply bg-live-red-100 border-live-red-200;
          }
        }
      }
    }

    :deep(.value-proposition) {
      .benefits {
        .headline {
          &::after {
            @apply bg-live-red-700;
          }
        }
      }
    }
  }

  &.sirocco {
    .gradient {
      background: linear-gradient(
        180deg,
        #fdf7f7 70.6%,
        rgba(253, 247, 247, 0) 100%
      );
    }

    :deep(.tag) {
      @apply bg-sirocco-100 text-sirocco-700;
    }

    :deep(strong) {
      @apply text-sirocco-700;
    }

    :deep(.jumbotron) {
      .hero {
        .tag {
          @apply bg-sirocco-200;
        }
      }

      .showcase {
        &::before {
          background: radial-gradient(
            50% 50% at 50% 50%,
            #fdede7 0%,
            rgba(253, 237, 231, 0) 100%
          );
        }
      }
    }

    :deep(.benefits) {
      .inner {
        @apply bg-sirocco-100;
      }
    }

    :deep(.stats) {
      .stats-banner {
        .highlighted {
          @apply bg-sirocco-500;
        }
      }
    }

    :deep(.features) {
      .list {
        .item {
          .illustration {
            @apply bg-sirocco-100 border-sirocco-200;
          }
        }
      }
    }

    :deep(.value-proposition) {
      .benefits {
        .headline {
          &::after {
            @apply bg-sirocco-700;
          }
        }
      }
    }
  }

  &.spring-green {
     .gradient {
      background: linear-gradient(180deg, #F5FAF9 70.60%, rgba(245, 250, 249, 0.00) 100%);
    }

    :deep(.tag) {
      @apply bg-spring-green-100 text-spring-green-800;
    }

    :deep(strong) {
      @apply text-spring-green-800;
    }

    :deep(.jumbotron) {
      .hero {
        .tag {
          @apply bg-spring-green-200;
        }
      }

      .showcase {
        &::before {
          background: radial-gradient(
            50% 50% at 50% 50%,
            rgba(253, 237, 231, 1) 0%,
            rgba(253, 237, 231, 0) 100%
          );
        }
      }
    }

    :deep(.benefits) {
      .inner {
        @apply bg-spring-green-100;
      }
    }

    :deep(.stats) {
      .stats-banner {
        .highlighted {
          @apply bg-spring-green-500;
        }
      }
    }

    :deep(.features) {
      .list {
        .item {
          .illustration {
            @apply bg-spring-green-100 border-spring-green-200;
          }
        }
      }
    }

    :deep(.value-proposition) {
      .benefits {
        .headline {
          &::after {
            @apply bg-spring-green-700;
          }
        }
      }
    }
  }

  &.sunburst-red {
    .gradient {
      background: linear-gradient(180deg, #FDF7F7 70.60%, rgba(253, 247, 247, 0.00) 100%);
    }

    :deep(.tag) {
      @apply bg-sunburst-red-100 text-sunburst-red-800;
    }

    :deep(strong) {
      @apply text-sunburst-red-700;
    }

    :deep(.jumbotron) {
      .hero {
        .tag {
          @apply bg-sunburst-red-200;
        }
      }

      .showcase {
        &::before {
          background: radial-gradient(
            50% 50% at 50% 50%,
            #fdede7 0%,
            rgba(253, 237, 231, 0) 100%
          );
        }
      }
    }

    :deep(.benefits) {
      .inner {
        @apply bg-sunburst-red-200;
      }
    }

    :deep(.stats) {
      .stats-banner {
        .highlighted {
          @apply bg-sunburst-red-500;
        }
      }
    }

    :deep(.features) {
      .list {
        .item {
          .illustration {
            @apply bg-sunburst-red-100 border-sunburst-red-200;
          }
        }
      }
    }

    :deep(.value-proposition) {
      .benefits {
        .headline {
          &::after {
            @apply bg-sunburst-red-700;
          }
        }
      }
    }
  }

  &.winter-green {
    .gradient {
      background: linear-gradient(
        180deg,
        #f1f7f9 0%,
        rgba(241, 247, 249, 0) 100%
      );
    }

    :deep(.tag) {
      @apply bg-winter-green-100 text-winter-green-600;
    }

    :deep(strong) {
      @apply text-winter-green-600;
    }

    :deep(.jumbotron) {
      .hero {
        .tag {
          @apply bg-winter-green-200;
        }
      }

      .showcase {
        &::before {
          background: radial-gradient(
            50% 50% at 50% 50%,
            #d0e6eb 0%,
            rgba(208, 230, 235, 0) 100%
          );
        }
      }
    }

    :deep(.benefits) {
      .inner {
        @apply bg-winter-green-100;
      }
    }

    :deep(.stats) {
      .stats-banner {
        .highlighted {
          @apply bg-winter-green-500;
        }
      }
    }

    :deep(.features) {
      .list {
        .item {
          .illustration {
            @apply bg-winter-green-100 border-winter-green-200;
          }
        }
      }
    }

    :deep(.value-proposition) {
      .benefits {
        .headline {
          &::after {
            @apply bg-winter-green-700;
          }
        }
      }
    }
  }

  .gradient {
    @apply absolute left-0 right-0 z-0;
    height: 1580px;
    top: -56px;

    @screen md {
      top: -72px;
    }

    @screen xl {
      height: 1056px;
    }
  }

  .jumbotron {
    @apply flex-col pt-12 pb-20 z-10 overflow-hidden;

    @screen md {
      @apply pb-34;
    }

    @screen lg {
      @apply flex-row flex-wrap justify-between items-center pt-16 overflow-visible;
    }

    .content {
      @apply flex-col;

      @screen lg {
        @apply w-span5;
      }

      :deep(.hero) {
        .h1 {
          @screen mdminus {
            @apply text-3xl leading-10;
          }

          @screen mdplus {
            @apply text-5xl leading-16;
          }
        }
      }

      :deep(.review) {
        @apply flex-col items-center mt-8;

        @screen xl {
          @apply items-start;
        }

        .rating {
          @apply relative justify-center w-auto cursor-pointer;

          @screen xl {
            @apply justify-start;
          }

          &::before,
          &::after {
            @apply opacity-0 invisible transition-all duration-300 ease-linear;
          }

          &::before {
            @apply absolute flex justify-center items-center bg-grey-blue-100 text-center text-origin text-winter-green-900 leading-7 shadow-light4 z-20;
            content: attr(data-hover);
            border-radius: 12px;
            padding: 14px 24px;
            transform: translateY(calc(-100% - 1rem));
          }

          &::after {
            @apply absolute flex h-5 w-5 bg-grey-blue-100 transform origin-center rotate-45 z-30;
            content: "";
            border-radius: 3px;
            left: calc(50% - 10px);
            top: -30px;
          }

          &:hover {
            &::before,
            &::after {
              @screen md {
                @apply opacity-100 visible;
              }
            }
          }

          .stars {
            @apply w-auto;
            margin-right: 10px;
          }

          .copy {
            @apply flex w-auto whitespace-pre;

            p {
              @apply text-center text-origin text-grey-blue-800 leading-7;

              @screen lg {
                @apply text-left;
              }

              strong {
                @apply font-medium text-grey-blue-800 !important;
              }
            }
          }
        }

        .customers {
          @apply justify-center flex-wrap gap-x-8 gap-y-6 mt-6;

          @screen md {
            @apply max-w-[624px];
          }

          @screen xl {
            @apply justify-start gap-4 max-w-full;
          }

          .logo {
            @apply flex-shrink-0 justify-center w-auto;

            picture {
              @apply rounded-none;

              img {
                filter: invert(45%) sepia(13%) saturate(601%) hue-rotate(163deg)
                  brightness(87%) contrast(84%);
              }
            }
          }
        }
      }
    }

    .showcase {
      @apply relative mt-12;

      @screen lg {
        @apply w-span6 mt-0;
      }

      &::before {
        @apply absolute flex transform -translate-x-1/2 -translate-y-1/2 z-0;
        content: "";
        height: 415px;
        width: 415px;
        top: 50%;
        left: 50%;

        @screen md {
          height: 751px;
          width: 751px;
        }

        @screen xl {
          height: 755px;
          width: 755px;
        }
      }

      .cover {
        @apply rounded-xs;

        @screen md {
          @apply rounded;
        }
      }
    }
  }

  .benefits {
    @apply z-10;

    .inner {
      @apply relative flex-col p-8 bg-livestorm-blue-200 shadow-light3 rounded-4xl;

      @screen md {
        @apply p-12;
      }

      @screen lg {
        @apply p-16;
      }

      picture {
        @apply absolute inset-0 rounded-4xl z-0;
      }

      :deep(.hero) {
        @apply z-10;

        p {
          @apply text-grey-blue-700;
        }
      }

      .list {
        @apply flex-col mt-16 z-10;

        @screen xl {
          @apply flex-row flex-wrap justify-between;
        }

        .item {
          @apply flex-col mt-8;

          @screen xl {
            @apply mt-0;
            max-width: 357px;
          }

          &:first-child {
            @apply mt-0;
          }

          .h3 {
            @apply text-xl leading-7;

            @screen mdminus {
              @apply text-2xl leading-8;
            }

            @screen mdplus {
              @apply text-2xl leading-8;
            }

            & + p {
              @apply mt-4 text-grey-blue-700;
            }
          }
        }
      }
    }
  }

  .related-sub-industries {
    @apply flex-col mt-14 gap-10;

    @screen md {
      @apply mt-26;
    }

    .list {
      @apply flex-col gap-10;

      @screen md {
        @apply gap-6;
      }

      @screen xl {
        @apply flex-row flex-wrap justify-center gap-10;
      }

      .sub-industry-card {
        @screen xl {
          width: calc((100% - 80px) / 3);
        }
      }
    }
  }

  .stats {
    @apply flex-col mt-20;

    @screen md {
      @apply mt-34;
    }
  }

  .features {
    @apply flex-col mt-20;

    @screen md {
      @apply mt-34;
    }

    .list {
      @apply flex-col gap-20 mt-14;

      @screen md {
        @apply gap-[136px] mt-26;
      }

      .item {
        @apply flex-col;

        @screen xl {
          @apply flex-row flex-wrap items-center justify-between;
        }

        &:nth-child(odd) {
          .text {
            @screen xl {
              @apply order-last;
            }
          }
        }

        .text {
          @apply flex-col;

          @screen xl {
            @apply w-span4;
          }

          .h3 {
            @apply text-xl leading-7;

            @screen mdminus {
              @apply text-2xl leading-8;
            }

            @screen mdplus {
              @apply text-2xl leading-8;
            }

            & + p {
              @apply mt-4 text-grey-blue-600;
            }
          }
        }

        .illustration {
          @apply mt-6 border-[0.5px] rounded-2xl;

          @screen xl {
            @apply w-span7 mt-0 rounded-4xl;
          }
        }
      }
    }
  }

  .value-proposition {
    @apply mt-14;

    @screen md {
      @apply mt-34;
    }
  }

  .use-cases {
    @apply flex-col items-center mt-26;

    @screen md {
      @apply mt-46;
    }

    &.extended {
      .desktop {
        .slider {
          .inner {
            @apply shadow-light2;
          }
        }
      }
    }

    :deep(.hero) {
      h2 {
        @apply font-normal !important;

        @screen md {
          @apply text-5xl leading-16;
        }

        @screen xl {
          max-width: 1000px;
        }

        strong,
        b {
          @apply font-normal !important;
        }
      }
    }

    .mobile {
      @apply flex-col mt-14;

      @screen md {
        @apply flex-row flex-wrap mt-20;
      }

      @screen xl {
        @apply hidden;
      }

      .use-case {
        @apply flex-col mt-14;

        &:first-child {
          @apply mt-0;
        }

        &.blizzard {
          .background {
            @apply bg-blizzard-100 border-blizzard-300;

            i {
              @apply text-blizzard-500;
            }
          }
        }

        &.grey-blue {
          .background {
            @apply bg-grey-blue-100 border-grey-blue-300;

            i {
              @apply text-grey-blue-500;
            }
          }
        }

        &.livestorm-blue {
          .background {
            @apply bg-livestorm-blue-100 border-livestorm-blue-300;

            i {
              @apply text-livestorm-blue-500;
            }
          }
        }

        &.live-red {
          .background {
            @apply bg-live-red-100 border-live-red-300;

            i {
              @apply text-live-red-500;
            }
          }
        }

        &.sirocco {
          .background {
            @apply bg-sirocco-100 border-sirocco-300;

            i {
              @apply text-sirocco-500;
            }
          }
        }

        &.sunburst-red {
          .background {
            @apply bg-sunburst-red-100 border-sunburst-red-300;

            i {
              @apply text-sunburst-red-500;
            }
          }
        }

        @screen md {
          @apply mt-16;
          width: calc(calc(100% - 2.5rem) / 2);

          &:nth-child(1),
          &:nth-child(2) {
            @apply mt-0;
          }

          &:nth-child(odd) {
            @apply ml-0 mr-5;
          }

          &:nth-child(even) {
            @apply ml-5 mr-0;
          }
        }

        .background {
          @apply items-center justify-center h-14 w-14 border-2;
          border-radius: 16px;

          i {
            @apply text-2xl leading-none;
          }
        }

        .h3 {
          @apply mt-4 font-medium text-origin leading-7;

          @screen mdminus {
            @apply text-xl;
          }

          @screen mdplus {
            @apply text-xl;
          }
        }

        .h3 + p {
          @apply mt-2 text-grey-blue-600;
        }

        .button {
          @apply self-start mt-4 px-0 font-semibold;
        }
      }
    }

    .desktop {
      @apply relative hidden;

      @screen xl {
        @apply flex flex-col mt-8;
      }

      .list {
        @apply flex-wrap justify-center;

        .item {
          @apply items-center w-auto h-11 mb-4 px-3 bg-transparent leading-11 cursor-pointer;
          border-radius: 1rem;

          &.active {
            @apply bg-grey-blue-100 cursor-default;

            p {
              @apply text-grey-blue-900;
            }
          }

          p {
            @apply font-medium text-grey-blue-600;
          }
        }
      }

      .slider {
        @apply mt-6;

        .inner {
          @apply rounded-4xl;

          .use-case {
            @apply flex flex-row flex-wrap items-center justify-between p-12;

            &.blizzard {
              @apply bg-blizzard-100;

              .background {
                @apply border-blizzard-300;

                i {
                  @apply text-blizzard-500;
                }
              }
            }

            &.grey-blue {
              @apply bg-grey-blue-100;

              .background {
                @apply border-grey-blue-300;

                i {
                  @apply text-grey-blue-500;
                }
              }
            }

            &.livestorm-blue {
              @apply bg-livestorm-blue-100;

              .background {
                @apply border-livestorm-blue-300;

                i {
                  @apply text-livestorm-blue-500;
                }
              }
            }

            &.live-red {
              @apply bg-live-red-100;

              .background {
                @apply border-live-red-300;

                i {
                  @apply text-live-red-500;
                }
              }
            }

            &.sirocco {
              @apply bg-sirocco-100;

              .background {
                @apply border-sirocco-300;

                i {
                  @apply text-sirocco-500;
                }
              }
            }

            &.sunburst-red {
              @apply bg-sunburst-red-100;

              .background {
                @apply border-sunburst-red-300;

                i {
                  @apply text-sunburst-red-500;
                }
              }
            }

            .content {
              @apply flex-col;
              max-width: 462px;

              .background {
                @apply items-center justify-center h-14 w-14 border-2;
                border-radius: 16px;

                i {
                  @apply text-2xl leading-none;
                }
              }

              .h3 {
                @apply mt-4 font-medium text-origin leading-7;

                @screen mdminus {
                  @apply text-xl;
                }

                @screen mdplus {
                  @apply text-xl;
                }
              }

              .h3 + p {
                @apply mt-2 text-grey-blue-600;
              }

              .button {
                @apply self-start mt-4 px-0 font-semibold;
              }
            }

            .picture {
              @apply rounded;
              max-width: 682px;
            }
          }
        }
      }
    }

    .product {
      @apply flex-col mt-20 p-8 bg-grey-blue-100 rounded-4xl;

      @screen md {
        @apply p-10 pb-6;
      }

      @screen xl {
        @apply -mt-8 pt-18 rounded-t-none shadow-light1;
      }

      .h3 {
        @apply text-origin leading-7;

        @screen mdminus {
          @apply text-xl;
        }

        @screen mdplus {
          @apply text-xl;
        }
      }

      .list {
        @apply flex-col mt-4;

        @screen md {
          @apply flex-row flex-wrap;
        }

        .item {
          @apply inline-flex self-start items-center w-auto mt-4 px-4 py-2 bg-livestorm-blue-200 font-medium text-grey-blue-800 text-origin leading-7 backdrop-blur-[6px] rounded-xs transition-colors duration-500 ease-in-out;

          @screen md {
            @apply mt-0 mb-4 mr-4;
          }

          &:first-child {
            @apply mt-0;
          }

          &:hover {
            @apply bg-livestorm-blue-300;
          }

          .icon {
            @apply mr-2;
            filter: brightness(0) saturate(100%) invert(26%) sepia(97%)
              saturate(5570%) hue-rotate(224deg) brightness(101%) contrast(99%);
          }
        }
      }
    }
  }

  .frequently-asked-question {
    @apply justify-center mt-20;

    @screen md {
      @apply mt-34;
    }

    .inner {
      @apply flex-col items-center;

      .h2 {
        @apply text-center;
      }

      .accordion {
        @apply mt-10;
      }

      .button {
        @apply mt-9;
      }
    }
  }

  .reviews {
    @apply flex-col flex-wrap mt-20;

    @screen md {
      @apply mt-34;
    }

    @screen xl {
      @apply flex-row;
    }

    .review-g2 {
      @screen xl {
        @apply w-span7 ml-0 mr-5;
      }

      :deep(.tag) {
        @apply bg-livestorm-blue-100 text-livestorm-blue-700;
      }
    }

    .testimonials {
      @apply mt-10;

      @screen md {
        @apply mt-12;
      }

      @screen xl {
        @apply w-span5 ml-5 mr-0 mt-0;
      }
    }
  }

  .get-started {
    @apply mt-26 overflow-hidden;

    @screen md {
      @apply mt-46;
    }
  }

  .seo-items {
    @apply mt-26;

    @screen md {
      @apply mt-46;
    }

    .list {
      @apply flex-col;

      .item {
        @apply flex-col mt-6;

        &:first-child {
          @apply mt-0;
        }

        :deep(.text) {
          @apply mt-1 text-grey-blue-600 text-base leading-[26px];

          a {
            @apply text-livestorm-blue-700;
          }

          b,
          strong {
            @apply font-medium text-grey-blue-600;
          }

          ol {
            li {
              @apply font-normal text-grey-blue-600 text-base leading-[26px] list-decimal list-inside;
            }
          }

          ul {
            li {
              @apply block font-normal text-grey-blue-600 text-base leading-[26px];

              &:before {
                @apply inline-block w-3 h-3 bg-livestorm-blue-700 mr-3;
                border-radius: 3px;
                content: "";
              }
            }
          }

          p + p,
          p + ol,
          p + ul,
          ol + ol,
          ol + p,
          ul + ul,
          ul + p {
            @apply mt-1;
          }
        }

        .image {
          @apply justify-center mt-6;

          .inner {
            @apply relative max-w-[840px] bg-grey-blue-100 rounded-4xl overflow-hidden;
          }
        }

        .video {
          @apply justify-center mt-6;

          .inner {
            @apply relative max-w-[840px] bg-grey-blue-100 aspect-video rounded-4xl overflow-hidden;

            iframe {
              @apply absolute h-full w-full flex;
            }
          }
        }
      }
    }
  }
}
</style>
